import { EnvironmentFacade } from '@mode/shared/environment/contract';
import * as angular from 'angular';
import { firstValueFrom } from 'rxjs';

const PAYMENT_DETAILS_COOKIE_KEY = 'payment-method-details';
const RECURLY_TOKEN_ERRORS = {
  validation: {
    first_name: 'First name is required',
    last_name: 'Last name is required',
    address: 'Address is required',
    city: 'City is required',
    zip: 'Postal Code is required',
    state: 'State/Region is required',
  },
};

function displayError(elem: JQuery, message?: string) {
  elem
    .parents('.form-group')
    .addClass('has-error')
    .find('.help-block')
    .html(message || elem.data('error'));
}

function hideAllErrors(elem: JQuery) {
  const errorElements = elem.find('.error-message, #generic-error');
  errorElements.addClass('hide');
  elem.find('.form-group.has-error .help-block').html('');
  elem.find('.form-group.has-error').removeClass('has-error');
}

function hasValidInputs($elem: JQuery): boolean {
  const firstName = $elem.find('#first_name');
  const lastName = $elem.find('#last_name');
  const address = $elem.find('#address1');
  const city = $elem.find('#city');
  const state = $elem.find('#state');
  const zip = $elem.find('#zip');

  // assume all inputs are valid
  let isValid = true;

  // then run thru each, if its invalid update isValid variable and display error in UI
  if (!firstName.val().trim().length) {
    isValid = false;
    displayError(firstName);
  }

  if (!lastName.val().trim().length) {
    isValid = false;
    displayError(lastName);
  }

  if (!address.val().trim().length) {
    isValid = false;
    displayError(address);
  }

  if (!city.val().trim().length) {
    isValid = false;
    displayError(city);
  }

  if (!state.val().trim().length) {
    isValid = false;
    displayError(state);
  }

  if (!zip.val().trim().length) {
    isValid = false;
    displayError(zip);
  }

  if (!$elem.find('#agree-tos').is(':checked')) {
    isValid = false;
    displayError($elem.find('#agree-tos'));
  }

  return isValid;
}

function displayGenericError($elem: JQuery) {
  const errorElements = $elem.find('.error-message, #generic-error');
  errorElements.removeClass('hide');
}

function setupAndDisplaySecureChallenge(threeDToken, $elem, $cookies) {
  // target container for sca challenge
  const challengeElementContainer = document.querySelector('#three-d-challenge-container');
  // configure threeDSecure
  const risk = recurly.Risk();
  const threeDSecure = risk.ThreeDSecure({ actionTokenId: threeDToken });

  // then attach the challenge to container and hide the billing form
  threeDSecure.attach(challengeElementContainer);
  $elem.addClass('hide');

  // if there is an error, show generic err
  threeDSecure.on('error', (err) => {
    console.error(err);
    displayGenericError($elem);
  });
  // if the threeDSecure challenge is successfull
  threeDSecure.on('token', (token) => {
    // add an input to the form to pass success token
    $elem.append(`<input type="hidden" name="${token.type}" value="${token.id}" /> `);
    // hide the challenge
    $(challengeElementContainer).addClass('hide');
    // and show the original form
    readCookiesAfterChallenge($elem, $cookies);
    $elem.removeClass('hide');
  });
}

function storeCookiesBeforeChallenge($elem, $cookies) {
  var expiresValue = new Date();
  expiresValue.setMinutes(new Date().getMinutes() + 1);
  $cookies.putObject(PAYMENT_DETAILS_COOKIE_KEY, {
    firstName: $elem.find('#first_name').val(),
    lastName: $elem.find('#last_name').val(),
    country: $elem.find('#country').val(),
    address1: $elem.find('#address1').val(),
    city: $elem.find('#city').val(),
    state: $elem.find('#state').val(),
    zip: $elem.find('#zip').val(),
  });
}

function readCookiesAfterChallenge($elem, $cookies) {
  var paymentMethodDetails = $cookies.getObject(PAYMENT_DETAILS_COOKIE_KEY);
  $elem.find('#first_name').val(paymentMethodDetails.firstName);
  $elem.find('#last_name').val(paymentMethodDetails.lastName);
  $elem.find('#country').val(paymentMethodDetails.country);
  $elem.find('#address1').val(paymentMethodDetails.address1);
  $elem.find('#city').val(paymentMethodDetails.city);
  $elem.find('#state').val(paymentMethodDetails.state);
  $elem.find('#zip').val(paymentMethodDetails.zip);
}

function collectPaymentMethodPage() {
  let paymentFormElement = angular.element('#collect-payment-form');
  return paymentFormElement.length ? true : false;
}

function configureStates(serivceHelper) {
  if (!collectPaymentMethodPage()) return;

  const countryField = angular.element('#country');
  fetchAndUpdateStates(countryField, serivceHelper);
  countryField.on('change', function () {
    fetchAndUpdateStates(countryField, serivceHelper);
  });
}

function getSelectedCountryName(countryField) {
  let selectedIndex = countryField.prop('selectedIndex');
  let selectedOption = countryField.find('option').eq(selectedIndex);
  return selectedOption.text();
}

function updateStatesDropDown(response) {
  const stateDropDown = document.getElementById('state');
  for (let i = 0; i < response.length; i++) {
    var option = document.createElement('option');
    option.text = response[i][0];
    option.value = response[i][1];
    stateDropDown.appendChild(option);
  }
}

function resetStatesDropDown() {
  angular.element('#state').empty();
  const defaultOptions = [['Select State', '']];
  updateStatesDropDown(defaultOptions);
}

function fetchAndUpdateStates(countryField, serivceHelper) {
  let countryName = getSelectedCountryName(countryField);
  const statesDataPromise = serivceHelper.getStatesByCountry(countryName);
  statesDataPromise
    .then((response) => {
      resetStatesDropDown();
      updateStatesDropDown(response);
    })
    .catch(() => {
      resetStatesDropDown();
    });
}

function PaymentInfoValidator($cookies, StatesDataService, environmentFacade: EnvironmentFacade) {
  return {
    restrict: 'A',
    link: function ($scope, $elem: JQuery, $attrs) {
      // Configure Country Dropdown
      configureStates(StatesDataService);

      firstValueFrom(environmentFacade.recurlyPublicKey$).then((recurlyKey) => {
        if (!recurly.configured) {
          recurly.configure(recurlyKey);
        }

        // check to see if backend has supplied a threeDToken for SCA challenge
        if ($attrs.threeDToken) {
          // if there is, init the challenge
          setupAndDisplaySecureChallenge($attrs.threeDToken, $elem, $cookies);
        }

        // append recurly card element
        const elements = recurly.Elements();
        let cardElement;
        if (collectPaymentMethodPage()) {
          cardElement = elements.CardElement({
            displayIcon: false,
            style: {
              border: '1px solid red',
            },
          });
        } else {
          cardElement = elements.CardElement();
        }
        cardElement.attach('#recurly-elements');

        // then listen for when the form is about to submit
        $elem.on('submit', function (event) {
          const form = this;
          event.preventDefault();

          // assume there are no errors and hide all errors in UI
          hideAllErrors($elem);
          // then check for errors
          let currentInputsAreValid = hasValidInputs($elem);

          // if inputs are valid
          if (currentInputsAreValid) {
            // using the recurly token, attempt to send form
            recurly.token(elements, form, function (err, token) {
              if (err) {
                // in case of generic error, throw error in console
                console.error(err);
                // and display generic error UI
                displayGenericError($elem);
              } else {
                // else store input values in case three_d_secure_action is needed
                storeCookiesBeforeChallenge($elem, $cookies);
                // and then submit payment form
                form.submit();
              }
            });
          }
        });
        //
      });
    },
  };
}

PaymentInfoValidator.$inject = ['$cookies', 'StatesDataService', 'EnvironmentFacade'];
angular.module('mode.components').directive('paymentInfoValidator', PaymentInfoValidator);
